<script>
  import QRCode from 'qrcode';

  import { getBackgroundStyle, parseImage, parseQrCode } from '@client/pages/brandkit/utils';

  import UserSilhouette from './user-silhouette.svelte';

  export let user = {};
  export let showUser = false;
  export let width = 0;
  export let height = 0;

  export let background;
  export let foreground;
  export let cells = Array(9)
    .fill(null)
    .map(() => []);

  let innerWidth = 0;
  let innerHeight = 0;
  let scale = 1;

  const processCell = (value, user) => {
    return value.replace(
      /\{\{\s*(\w+)\s*(?:\|\s*default:\s*(?:"([^"]*)"|(\w+)))?\s*\}\}/g,
      (_, key, literalDefault, fieldDefault) => {
        if (user && user[key] !== undefined) return user[key];
        if (literalDefault !== undefined) return literalDefault;
        if (fieldDefault !== undefined) return user[fieldDefault] ?? value;
        return value;
      }
    );
  };

  const generateQrCode = (url) => QRCode.toDataURL(url, { width: 200, margin: 1 });

  /** @param {number} index */
  const getObjectPosition = (index) => {
    if (index < 0 || index > 8 || index === 4) return 'center';
    const columns = ['left', 'center', 'right'];
    const rows = ['top', 'center', 'bottom'];
    return `${columns[index % 3]} ${rows[Math.floor(index / 3)]}`;
  };

  $: scale = innerWidth && innerHeight && width && height ? Math.min(1, innerWidth / width, innerHeight / height) : 0;
</script>

<div class="viewport" bind:clientWidth={innerWidth} bind:clientHeight={innerHeight}>
  <div
    class="grid"
    style:background={getBackgroundStyle(background)}
    style:color={foreground}
    style:height="{height}px"
    style:transform="scale({scale})"
    style:width="{width}px"
  >
    {#each cells as cell, index}
      <div>
        {#each cell as item, lineNumber}
          {@const [src, scale] = parseImage(item)}
          {@const [qr, qrScale] = parseQrCode(item)}
          {#if item.startsWith('http')}
            <img alt={item} src={item} />
          {:else if src}
            <img
              alt={src}
              {src}
              style:transform="scale({scale})"
              style:transform-origin={getObjectPosition(index)}
            />
          {:else if qr}
            {#await generateQrCode(qr)}
              <img alt="QR Code" src="" class="qr-placeholder" />
            {:then dataUrl}
              <img
                alt={dataUrl}
                src={dataUrl}
                style:transform="scale({qrScale})"
                style:transform-origin={getObjectPosition(index)}
              />
            {:catch}
              <img alt="QR Code error" src="" class="qr-placeholder" />
            {/await}
          {:else}
            <svelte:element this={`h${Math.min(lineNumber + 1, 6)}`}>
              <span>{processCell(item, user)}</span>
            </svelte:element>
          {/if}
        {/each}
      </div>
    {/each}

    {#if showUser}
      <UserSilhouette opacity={0.67} />
    {/if}
  </div>
</div>

<style lang="scss">
  .viewport {
    display: grid;
    height: 100%;
    overflow: hidden;
    width: 100%;
  }

  .grid {
    background-color: white;
    border: 1px solid black;
    display: grid;
    grid-template-columns: repeat(3, calc(100% / 3));
    grid-template-rows: repeat(3, calc(100% / 3));
    height: 100%;
    transform-origin: top left;
    width: 100%;

    div {
      display: flex;
      flex-direction: column;
      flex-wrap: wrap;
      gap: 8px;
      padding: 8px;

      // Column 1
      &:nth-child(3n + 1) {
        align-items: flex-start;
        padding-left: 7.5%;
      }

      // Column 2
      &:nth-child(3n + 2) {
        align-items: center;
      }

      // Column 3
      &:nth-child(3n) {
        align-items: flex-end;
        padding-right: 7.5%;
      }

      // Row 1
      &:nth-child(-n + 3) {
        justify-content: flex-start;
        padding-top: 7.5%;
      }

      // Row 2
      &:nth-child(n + 4):nth-child(-n + 6) {
        justify-content: center;
      }

      // Row 3
      &:nth-child(n + 7) {
        justify-content: flex-end;
        padding-bottom: 7.5%;
      }

      &:nth-child(1) img {
        object-position: left top;
      }

      &:nth-child(2) img {
        object-position: center top;
      }

      &:nth-child(3) img {
        object-position: right top;
      }

      &:nth-child(4) img {
        object-position: left center;
      }

      &:nth-child(5) img {
        object-position: center;
      }

      &:nth-child(6) img {
        object-position: right center;
      }

      &:nth-child(7) img {
        object-position: left bottom;
      }

      &:nth-child(8) img {
        object-position: center bottom;
      }

      &:nth-child(9) img {
        object-position: right bottom;
      }
    }

    h1 {
      font-size: 4rem;
    }
    h2 {
      font-size: 3rem;
    }
    h3 {
      font-size: 2.33rem;
    }
    h4 {
      font-size: 2rem;
    }
    h5 {
      font-size: 1.67rem;
    }
    h6 {
      font-size: 1.5rem;
    }

    img {
      max-width: 100%;
      max-height: 100%;
      width: 100%;
      height: 100%;
      object-fit: contain;
      display: block;
    }
  }
</style>
